import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { auth } from '../../firebase';
import { signOut } from 'firebase/auth';
import { Home, BookOpen, Users, Settings, LogOut, Sun, Moon, Star, ExternalLink, Globe, Bell, FileEdit } from 'lucide-react';
import { useState } from 'react';
import DashboardTopBar from './DashboardTopBar';
import { useNotification } from '../../context/NotificationContext';

export default function InstructorDashboardLayout({ theme, toggleTheme }) {
  const { userData, currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { unreadCount } = useNotification();

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const navItems = [
    { path: '/instructor-dashboard', label: 'نظرة عامة', icon: Home },
    { path: '/instructor-dashboard/courses', label: 'دوراتي', icon: BookOpen },
    { path: '/instructor-dashboard/students', label: 'طلابي', icon: Users },
    { path: '/instructor-dashboard/assignments', label: 'التكاليف والمشاريع', icon: FileEdit },
    { path: '/instructor-dashboard/notifications', label: 'الإشعارات', icon: Bell, badge: unreadCount },
    { path: '/instructor-dashboard/reviews', label: 'التقييمات', icon: Star },
    { path: '/instructor-dashboard/settings', label: 'إعدادات الحساب', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#050505] flex flex-col md:flex-row transition-colors duration-500" dir="rtl">
      {/* Ambient Backdrop */}
      <div className="fixed inset-0 pointer-events-none bg-[url('https://transparenttextures.com/patterns/cubes.png')] opacity-[0.02] dark:opacity-5 mix-blend-overlay -z-10"></div>

      {/* Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/40 dark:bg-black/60 backdrop-blur-sm z-30 md:hidden transition-opacity duration-[400ms]"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed md:sticky top-0 right-0 z-40 w-72 h-screen backdrop-blur-xl bg-white/80 dark:bg-gradient-to-b dark:from-zinc-900/90 dark:to-zinc-900/90 border-l border-slate-200/50 dark:border-white/10 flex flex-col transition-transform duration-[400ms] ease-out shadow-[10px_0_50px_-15px_rgba(0,0,0,0.1)] dark:shadow-[10px_0_50px_-15px_rgba(0,0,0,0.5)] ${isSidebarOpen ? 'translate-x-0' : 'translate-x-full md:translate-x-0'}`}>

        {/* Logo Section */}
        <div className="p-8 pb-6 hidden md:block relative">
          <Link to="/" className="relative text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-l from-amber-500 to-amber-600 group flex items-center gap-2 drop-shadow-md">
            <span className="relative z-10 font-black tracking-tight">لُباب</span>
            <div className="absolute -inset-2 bg-amber-500/20 blur-xl rounded-full opacity-50 group-hover:opacity-80 animate-pulse transition-all duration-700 pointer-events-none"></div>
          </Link>
          <div className="mt-3 flex items-center gap-2">
            <span className="px-3 py-1 bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs font-extrabold rounded-full border border-amber-100 dark:border-amber-500/20">لوحة تحكم المدرب</span>
          </div>
        </div>

        {/* Instructor Quick Info */}
        <div className="px-5 pb-5 hidden md:flex items-center gap-3 border-b border-slate-200/30 dark:border-white/5 mx-5">
          <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-zinc-800 overflow-hidden border-2 border-amber-400/50 shrink-0 flex items-center justify-center">
            {userData?.photoURL ? (
              <img src={userData.photoURL} alt={userData.name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-lg font-black text-amber-600">{userData?.name?.charAt(0)}</span>
            )}
          </div>
          <div className="overflow-hidden">
            <p className="font-bold text-slate-900 dark:text-white text-sm truncate">{userData?.name}</p>
            <p className="text-xs text-slate-400 dark:text-zinc-500 font-medium truncate">{userData?.instructorData?.specialization || 'مدرب'}</p>
          </div>
        </div>

        {/* Navigation Section */}
        <div className="flex-1 overflow-y-auto py-6 px-5 no-scrollbar">
          <ul className="space-y-3">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path || (item.path !== '/instructor-dashboard' && location.pathname.startsWith(item.path));
              const Icon = item.icon;

              return (
                <li key={item.path} className="relative">
                  <Link
                    to={item.path}
                    onClick={() => setIsSidebarOpen(false)}
                    className={`flex items-center gap-4 px-5 py-4 rounded-xl transition-all duration-[400ms] group overflow-hidden relative ${isActive
                      ? 'bg-gradient-to-l from-amber-500/10 to-transparent border-r-4 border-amber-500 shadow-[inset_-10px_0_20px_rgba(245,158,11,0.05)]'
                      : 'hover:bg-slate-100/60 dark:hover:bg-white/5 border-r-4 border-transparent hover:border-slate-300 dark:hover:border-white/10'
                      }`}
                  >
                    {isActive && (
                      <div className="absolute inset-0 bg-amber-500/5 blur-[20px] scale-150 pointer-events-none"></div>
                    )}

                    <Icon className={`w-6 h-6 shrink-0 transition-transform duration-[400ms] ease-out group-hover:scale-110 relative z-10 ${isActive ? 'text-amber-500 drop-shadow-[0_0_12px_rgba(245,158,11,0.6)]' : 'text-slate-500 dark:text-slate-400 group-hover:text-slate-800 dark:group-hover:text-white'
                      }`} />

                    <span className={`text-[1.1rem] tracking-wide relative z-10 transition-colors duration-[400ms] ${isActive ? 'font-bold text-slate-900 dark:text-white drop-shadow-sm' : 'font-bold text-slate-600 dark:text-slate-400 group-hover:text-slate-900 dark:group-hover:text-white'
                      }`}>
                      {item.label}
                    </span>

                    {item.badge > 0 && (
                      <span className="mr-auto bg-amber-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center animate-pulse shadow-lg shadow-amber-500/30 relative z-10">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>

          {/* View Public Profile & Website Links */}
          {currentUser && (
            <div className="mt-6 px-2 space-y-2">
              <Link
                to={`/instructor/${userData?.username || currentUser.uid}`}
                target="_blank"
                className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200/50 dark:border-white/5 text-slate-500 dark:text-zinc-400 hover:text-amber-600 dark:hover:text-amber-400 hover:border-amber-200 dark:hover:border-amber-500/20 transition-all font-bold text-sm group"
              >
                <ExternalLink className="w-4 h-4 group-hover:scale-110 transition-transform" />
                عرض صفحتي العامة
              </Link>
              <Link
                to="/"
                target="_blank"
                className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200/50 dark:border-white/5 text-slate-500 dark:text-zinc-400 hover:text-slate-800 dark:hover:text-white hover:border-slate-300 dark:hover:border-white/10 transition-all font-bold text-sm group"
              >
                <Globe className="w-4 h-4 group-hover:scale-110 transition-transform" />
                عرض صفحة الموقع
              </Link>
            </div>
          )}
        </div>

        {/* Bottom Actions Section */}
        <div className="p-7 border-t border-slate-200/50 dark:border-white/10 space-y-5 bg-slate-50/50 dark:bg-black/20">
          <div className="flex items-center justify-between px-2">
            <span className="text-[0.8rem] font-bold tracking-widest text-slate-500 dark:text-slate-400">إدارة الجلسات</span>
            <button
              onClick={toggleTheme}
              className="p-2.5 rounded-xl bg-white dark:bg-zinc-800/80 border border-slate-200 dark:border-white/5 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-zinc-700/80 transition-all duration-[400ms] hover:scale-105 shadow-sm"
              title="تغيير المظهر"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5 text-amber-500 drop-shadow-sm" /> : <Moon className="w-5 h-5 text-slate-700" />}
            </button>
          </div>

          <button
            onClick={handleLogout}
            className="group w-full flex items-center gap-4 px-5 py-4 rounded-xl text-slate-600 dark:text-slate-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 hover:text-rose-600 dark:hover:text-rose-500 transition-all duration-[400ms] border border-transparent hover:border-rose-100 dark:hover:border-rose-500/20"
          >
            <LogOut className="w-6 h-6 transition-transform duration-[400ms] group-hover:-translate-x-1" />
            <span className="text-[1.1rem] font-bold tracking-wide">تسجيل الخروج</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        <DashboardTopBar setSidebarOpen={setIsSidebarOpen} />

        <main className="flex-1 w-full max-w-[100rem] mx-auto p-4 md:p-8 overflow-y-auto relative z-10 transition-colors duration-500">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
